import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import api from '../utils/api';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { User, Lock, Mail, Shield, Save, Fingerprint, Trash2, CheckCircle, XCircle, Loader2, Smartphone } from 'lucide-react';
import {
  isWebAuthnSupported,
  isPlatformAuthenticatorAvailable,
  registerBiometric,
  getUserCredentials,
  deleteCredential,
} from '../utils/webauthn';

const Profile = () => {
  const { user } = useAuth();
  const { language } = useLanguage();
  const [loading, setLoading] = useState(false);
  const [biometricLoading, setBiometricLoading] = useState(false);
  const [webauthnSupported, setWebauthnSupported] = useState(false);
  const [platformAuthAvailable, setPlatformAuthAvailable] = useState(false);
  const [credentials, setCredentials] = useState([]);
  const [hasBiometric, setHasBiometric] = useState(false);
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  useEffect(() => {
    checkWebAuthnSupport();
    fetchCredentials();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const checkWebAuthnSupport = async () => {
    setWebauthnSupported(isWebAuthnSupported());
    const available = await isPlatformAuthenticatorAvailable();
    setPlatformAuthAvailable(available);
  };

  const fetchCredentials = async () => {
    if (!user?.id) return;
    const result = await getUserCredentials(user.id);
    setCredentials(result.credentials || []);
    setHasBiometric(result.has_biometric || false);
  };

  const handleRegisterBiometric = async () => {
    if (!user?.id) return;
    
    setBiometricLoading(true);
    try {
      const result = await registerBiometric(user.id);
      
      if (result.success) {
        toast.success(result.message || 'تم تسجيل البصمة بنجاح');
        fetchCredentials();
      } else {
        toast.error(result.error || 'فشل تسجيل البصمة');
      }
    } catch (error) {
      toast.error('حدث خطأ أثناء تسجيل البصمة');
    } finally {
      setBiometricLoading(false);
    }
  };

  const handleDeleteCredential = async (credentialId) => {
    const result = await deleteCredential(credentialId);
    
    if (result.success) {
      toast.success(result.message);
      fetchCredentials();
    } else {
      toast.error(result.error);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      toast.error(language === 'ar' ? 'كلمة المرور الجديدة غير متطابقة' : 'New passwords do not match');
      return;
    }

    if (passwordData.newPassword.length < 4) {
      toast.error(language === 'ar' ? 'كلمة المرور يجب أن تكون 4 أحرف على الأقل' : 'Password must be at least 4 characters');
      return;
    }

    setLoading(true);
    try {
      await api.post('/auth/change-password', {
        current_password: passwordData.currentPassword,
        new_password: passwordData.newPassword
      });
      toast.success(language === 'ar' ? 'تم تغيير كلمة المرور بنجاح' : 'Password changed successfully');
      setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || (language === 'ar' ? 'فشل تغيير كلمة المرور' : 'Failed to change password'));
    } finally {
      setLoading(false);
    }
  };

  const getRoleLabel = (role) => {
    const labels = {
      admin: language === 'ar' ? 'مدير' : 'Admin',
      barber: language === 'ar' ? 'حلاق' : 'Barber',
      guest: language === 'ar' ? 'عميل' : 'Customer'
    };
    return labels[role] || role;
  };

  return (
    <div className="space-y-6 max-w-2xl mx-auto">
      {/* Header */}
      <div className="text-center">
        <h1 className="text-3xl font-bold flex items-center justify-center gap-3">
          <User className="w-8 h-8 text-primary" />
          {language === 'ar' ? 'الملف الشخصي' : 'Profile'}
        </h1>
        <p className="text-muted-foreground mt-2">
          {language === 'ar' ? 'إدارة حسابك وتغيير كلمة المرور' : 'Manage your account and change password'}
        </p>
      </div>

      {/* User Info Card */}
      <div className="bg-card border border-white/5 rounded-xl shadow-2xl p-6">
        <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
          <Shield className="w-5 h-5 text-primary" />
          {language === 'ar' ? 'معلومات الحساب' : 'Account Information'}
        </h2>
        
        <div className="space-y-4">
          <div className="flex items-center gap-4 p-4 bg-secondary/30 rounded-lg">
            <div className="w-16 h-16 bg-primary/20 rounded-full flex items-center justify-center">
              <span className="text-2xl font-bold text-primary">
                {user?.name?.charAt(0)?.toUpperCase() || 'U'}
              </span>
            </div>
            <div>
              <p className="font-bold text-lg">{user?.name}</p>
              <p className="text-muted-foreground">{getRoleLabel(user?.role)}</p>
            </div>
          </div>

          <div className="grid gap-4">
            <div className="flex items-center gap-3 p-3 bg-secondary/20 rounded-lg">
              <User className="w-5 h-5 text-muted-foreground" />
              <div>
                <p className="text-sm text-muted-foreground">{language === 'ar' ? 'اسم المستخدم' : 'Username'}</p>
                <p className="font-medium" dir="ltr">{user?.email}</p>
              </div>
            </div>
            {user?.contact_email && (
              <div className="flex items-center gap-3 p-3 bg-secondary/20 rounded-lg">
                <Mail className="w-5 h-5 text-muted-foreground" />
                <div>
                  <p className="text-sm text-muted-foreground">{language === 'ar' ? 'البريد الإلكتروني' : 'Email'}</p>
                  <p className="font-medium" dir="ltr">{user?.contact_email}</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Change Password Card */}
      <div className="bg-card border border-white/5 rounded-xl shadow-2xl p-6">
        <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
          <Lock className="w-5 h-5 text-primary" />
          {language === 'ar' ? 'تغيير كلمة المرور' : 'Change Password'}
        </h2>

        <form onSubmit={handleChangePassword} className="space-y-4" data-testid="change-password-form">
          <div>
            <label className="block text-sm font-medium mb-2">
              {language === 'ar' ? 'كلمة المرور الحالية' : 'Current Password'}
            </label>
            <Input
              type="password"
              value={passwordData.currentPassword}
              onChange={(e) => setPasswordData({ ...passwordData, currentPassword: e.target.value })}
              placeholder="••••••••"
              required
              className="bg-secondary/50 border-white/10"
              data-testid="current-password-input"
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">
              {language === 'ar' ? 'كلمة المرور الجديدة' : 'New Password'}
            </label>
            <Input
              type="password"
              value={passwordData.newPassword}
              onChange={(e) => setPasswordData({ ...passwordData, newPassword: e.target.value })}
              placeholder="••••••••"
              required
              className="bg-secondary/50 border-white/10"
              data-testid="new-password-input"
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">
              {language === 'ar' ? 'تأكيد كلمة المرور الجديدة' : 'Confirm New Password'}
            </label>
            <Input
              type="password"
              value={passwordData.confirmPassword}
              onChange={(e) => setPasswordData({ ...passwordData, confirmPassword: e.target.value })}
              placeholder="••••••••"
              required
              className="bg-secondary/50 border-white/10"
              data-testid="confirm-password-input"
            />
          </div>

          <Button 
            type="submit" 
            className="w-full bg-primary text-primary-foreground"
            disabled={loading}
            data-testid="change-password-submit-btn"
          >
            <Save className="w-4 h-4 me-2" />
            {loading 
              ? (language === 'ar' ? 'جاري الحفظ...' : 'Saving...') 
              : (language === 'ar' ? 'حفظ كلمة المرور الجديدة' : 'Save New Password')
            }
          </Button>
        </form>
      </div>

      {/* Biometric Login Card */}
      <div className="bg-card border border-white/5 rounded-xl shadow-2xl p-6" data-testid="biometric-section">
        <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
          <Fingerprint className="w-5 h-5 text-primary" />
          {language === 'ar' ? 'تسجيل الدخول بالبصمة / Face ID' : 'Biometric / Face ID Login'}
        </h2>

        {/* Support Status */}
        <div className="space-y-3 mb-6">
          <div className="flex items-center justify-between p-3 bg-secondary/20 rounded-lg">
            <div className="flex items-center gap-2">
              <Smartphone className="w-5 h-5 text-muted-foreground" />
              <span className="text-sm">{language === 'ar' ? 'دعم WebAuthn' : 'WebAuthn Support'}</span>
            </div>
            {webauthnSupported ? (
              <CheckCircle className="w-5 h-5 text-green-500" />
            ) : (
              <XCircle className="w-5 h-5 text-red-500" />
            )}
          </div>
          
          <div className="flex items-center justify-between p-3 bg-secondary/20 rounded-lg">
            <div className="flex items-center gap-2">
              <Fingerprint className="w-5 h-5 text-muted-foreground" />
              <span className="text-sm">{language === 'ar' ? 'البصمة / Face ID متاح' : 'Biometric / Face ID Available'}</span>
            </div>
            {platformAuthAvailable ? (
              <CheckCircle className="w-5 h-5 text-green-500" />
            ) : (
              <XCircle className="w-5 h-5 text-red-500" />
            )}
          </div>
        </div>

        {/* Register Button */}
        {webauthnSupported && platformAuthAvailable ? (
          <div className="space-y-4">
            {!hasBiometric ? (
              <div className="text-center p-4 bg-primary/10 rounded-lg border border-primary/20">
                <Fingerprint className="w-12 h-12 text-primary mx-auto mb-3" />
                <p className="text-muted-foreground mb-4">
                  {language === 'ar' 
                    ? 'سجّل بصمتك للدخول السريع بدون كلمة مرور'
                    : 'Register your biometric for quick passwordless login'}
                </p>
                <Button
                  onClick={handleRegisterBiometric}
                  disabled={biometricLoading}
                  className="bg-primary text-primary-foreground"
                  data-testid="register-biometric-btn"
                >
                  {biometricLoading ? (
                    <Loader2 className="w-4 h-4 me-2 animate-spin" />
                  ) : (
                    <Fingerprint className="w-4 h-4 me-2" />
                  )}
                  {language === 'ar' ? 'تسجيل البصمة' : 'Register Biometric'}
                </Button>
              </div>
            ) : (
              <div className="space-y-3">
                <div className="flex items-center gap-2 text-green-500 mb-3">
                  <CheckCircle className="w-5 h-5" />
                  <span>{language === 'ar' ? 'البصمة مُسجلة' : 'Biometric Registered'}</span>
                </div>
                
                {/* List of credentials */}
                {credentials.map((cred) => (
                  <div 
                    key={cred.id} 
                    className="flex items-center justify-between p-3 bg-secondary/30 rounded-lg"
                  >
                    <div className="flex items-center gap-3">
                      <Fingerprint className="w-5 h-5 text-primary" />
                      <div>
                        <p className="text-sm font-medium">
                          {cred.device_type === 'single_device' ? 'جهاز واحد' : 'أجهزة متعددة'}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {new Date(cred.created_at).toLocaleDateString('ar-AE')}
                        </p>
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDeleteCredential(cred.id)}
                      className="text-red-500 hover:bg-red-500/10"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                ))}

                {/* Add another credential */}
                <Button
                  variant="outline"
                  onClick={handleRegisterBiometric}
                  disabled={biometricLoading}
                  className="w-full border-primary/50"
                >
                  {biometricLoading ? (
                    <Loader2 className="w-4 h-4 me-2 animate-spin" />
                  ) : (
                    <Fingerprint className="w-4 h-4 me-2" />
                  )}
                  {language === 'ar' ? 'إضافة جهاز آخر' : 'Add Another Device'}
                </Button>
              </div>
            )}
          </div>
        ) : (
          <div className="text-center p-4 bg-red-500/10 rounded-lg border border-red-500/20">
            <XCircle className="w-12 h-12 text-red-500 mx-auto mb-3" />
            <p className="text-muted-foreground">
              {!webauthnSupported 
                ? (language === 'ar' ? 'متصفحك لا يدعم البصمة' : 'Your browser does not support biometrics')
                : (language === 'ar' ? 'جهازك لا يدعم البصمة / Face ID' : 'Your device does not support biometrics / Face ID')
              }
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Profile;
